import {createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationNormalized,} from 'vue-router';
import {useUser} from '@/composables/useUser';

import DashboardLayout from '@/views/layouts/DashboardLayout.vue';
import AdminLayout from '@/views/layouts/AdminLayout.vue';
import AuthLayout from '@/views/layouts/AuthLayout.vue';

import DashboardHome from '@/views/dashboard/Home.vue';
import Course from '@/views/dashboard/Course.vue';
import Module from '@/views/dashboard/Module.vue';
import Account from '@/views/dashboard/Account.vue';
import Settings from '@/views/dashboard/Settings.vue';
import Directory from '@/views/dashboard/Directory.vue';
import Events from '@/views/dashboard/Events.vue';
import EventDetail from '@/views/dashboard/EventDetail.vue';
import AnnouncementDetail from '@/views/dashboard/AnnouncementDetail.vue';
import Academy from '@/views/dashboard/Academy.vue';


import AdministratorHome from '@/views/administrator/Home.vue';
import AdministratorCourses from '@/views/administrator/Courses.vue';
import AdministratorCourse from '@/views/administrator/Course.vue';
import AdministratorUsers from '@/views/administrator/Users.vue';
import AdministratorUser from '@/views/administrator/User.vue';
import AdministratorSettings from '@/views/administrator/Settings.vue';
import AdministratorEvents from '@/views/administrator/Events.vue';
import AdministratorAnnouncements from '@/views/administrator/Announcements.vue';
import AdministratorEventEdit from '@/views/administrator/EventEdit.vue';
import AdministratorAnnouncementEdit from '@/views/administrator/AnnouncementEdit.vue';
import AdministratorPages from '@/views/administrator/Pages.vue';
import AdministratorPageEdit from '@/views/administrator/PageEdit.vue';

import Login from '@/views/public/Login.vue';
import Register from '@/views/public/Register.vue';

const routes = [
    {
        path: '/',
        redirect: '/dashboard',
    },
    {
        path: '/',
        component: AuthLayout,
        children: [
            {path: 'login', name: 'login', component: Login},
            {path: 'register', name: 'register', component: Register},
        ],
    },
    {
        path: '/dashboard',
        component: DashboardLayout,
        children: [
            {path: '', name: 'dashboard-home', component: DashboardHome},
            {path: 'course/:id', name: 'dashboard-course', component: Course, props: true, meta: {requiresCommunityPlus: true}},
            {path: 'course/:courseId/:moduleId/:contentId', name: 'dashboard-module', component: Module, props: true, meta: {requiresCommunityPlus: true}},
            {path: 'account', name: 'dashboard-account', component: Account},
            {path: 'settings', name: 'dashboard-settings', component: Settings},
            {path: 'directory', name: 'dashboard-directory', component: Directory, meta: {requiresAdmin: true}},
            {path: 'events', name: 'dashboard-events', component: Events, meta: {public: true}},
            {path: 'events/:id', name: 'dashboard-event-detail', component: EventDetail, props: true, meta: {public: true}},
            {path: 'announcements/:id', name: 'dashboard-announcement-detail', component: AnnouncementDetail, props: true},
        ],
    },
    {
        path: '/academy',
        component: DashboardLayout,
        meta: {requiresCommunityPlus: true},
        children: [
            {path: '', name: 'academy', component: Academy},
        ],
    },
    {
        path: '/administrator',
        component: AdminLayout,
        meta: {requiresAdmin: true},
        children: [
            {path: '', name: 'admin-home', component: AdministratorHome},
            {path: 'courses', name: 'admin-courses', component: AdministratorCourses},
            {path: 'courses/course/:id', name: 'admin-course', component: AdministratorCourse, props: true},
            {path: 'users', name: 'admin-users', component: AdministratorUsers},
            {path: 'users/user/:id', name: 'admin-user', component: AdministratorUser, props: true},
            {path: 'events', name: 'admin-events', component: AdministratorEvents},
            {path: 'events/:id', name: 'admin-event-edit', component: AdministratorEventEdit, props: true},
            {path: 'announcements', name: 'admin-announcements', component: AdministratorAnnouncements},
            {path: 'announcements/:id', name: 'admin-announcement-edit', component: AdministratorAnnouncementEdit, props: true},
            {path: 'pages', name: 'admin-pages', component: AdministratorPages},
            {path: 'pages/new', name: 'admin-page-create', component: () => import('@/views/administrator/PageCreate.vue')},
            {path: 'pages/:id', name: 'admin-page-edit', component: AdministratorPageEdit, props: true},
            {path: 'settings', name: 'admin-settings', component: AdministratorSettings},
        ],
    },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior() {
        return {top: 0, left: 0};
    },
});

router.beforeEach(
    async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
        const {user, fetchUser} = useUser();

        if (!user.value) {
            await fetchUser();
        }

        if (!user.value && !to.matched.some(record => record.meta.public || record.path.startsWith('/login') || record.path.startsWith('/register'))) {
            return next({path: '/login'});
        }

        if (to.matched.some(record => record.meta.requiresAdmin) && user.value?.role !== 'admin') {
            return next({path: '/'});
        }

        if (to.matched.some(record => record.meta.requiresCommunityPlus)
            && user.value?.membershipPlan !== 'community_plus') {
            return next({path: '/dashboard'});
        }

        next();
    }
);

export default router;
